import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import readXlsxFile from 'read-excel-file';
import { getBusinessContacts } from 'store/projects/services/getBusinessContacts';
import { getBusinessIdPartnerConfiguration } from 'store/projects/services/getBusinessIdPartnerConfiguration';
import { getEnabledIntegrationPartnersByBusinessId } from 'store/projects/services/getEnabledIntegrationPartnersByBusinessId';
import { getProjectIlveability } from 'store/projects/services/getProjectIlveability';
import uuidv4 from 'uuid/v4';
import writeXlsxFile from 'write-excel-file';

function useProjectDialog(businessId) {
  const [contactOptions, setContactOptions] = useState([]);
  const [siteManagerOptions, setSiteManagerOptions] = useState([]);

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('common');

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const result = await getBusinessContacts(businessId);
        setContactOptions(result);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setContactOptions([]);
      }
    };

    fetchSuggestions();
  }, []);

  const getIlveability = async (parentId, projectId) => {
    try {
      const result = await getProjectIlveability(parentId, projectId);
      return result;
    } catch (error) {
      console.error('Error fetching ilveability:', error);
      enqueueSnackbar(t('project_dialog.snackbar_ilveability_failed'), {
        variant: 'error',
      });
      return false;
    }
  };

  const getIntegrationPartners = async () => {
    try {
      const results = [];
      const IntegrationPartnerResult =
        await getEnabledIntegrationPartnersByBusinessId(businessId);
      if (IntegrationPartnerResult && IntegrationPartnerResult.length > 0) {
        // console.log('Integration partner:', IntegrationPartnerResult);
        results.push(...IntegrationPartnerResult);
      }
      const partnerConfigurationResult =
        await getBusinessIdPartnerConfiguration(businessId);
      if (partnerConfigurationResult) {
        // console.log('Business ID Partner Configuration:', partnerConfigurationResult);
        results.push(partnerConfigurationResult);
      }
      return results;
    } catch (error) {
      console.error('Error fetching integration partner:', error);
      enqueueSnackbar(t('project_dialog.snackbar_integration_partner_failed'), {
        variant: 'error',
      });
      return [];
    }
  };

  const readAssignmentsFromXLS = async (file) => {
    console.log('Reading assignments from XLS');
    try {
      let rows = await readXlsxFile(file);
      const assignments = [];
      const lastAtDepth = {};
      rows.shift();
      console.log('Rows:', rows);

      rows.forEach((row) => {
        const depth = row.findIndex((item) => item !== null); // Depth is where the first non-null number is found
        const number = row[depth]; // This is the assignment number at this depth
        const name = row[depth + 1]; // Next item is the assignment name
        const unitAmountGoal = row[depth + 2]; // Next item is the unit amount goal
        const unit = row[depth + 3]; // Next item is the unit
        const unitPrice = row[depth + 4]; // Next item is the unit price

        const assignment = {
          id: uuidv4(), // Generate a unique ID
          number: number?.toString(),
          name: name?.toString() || '',
          unitAmountGoal: unitAmountGoal?.toString() || '',
          unit: unit?.toString() || '',
          unitPrice: unitPrice?.toString() || '',
          parent: null,
        };

        // If there's a parent at the level above, set it as the parent
        if (depth > 0 && lastAtDepth[depth - 1]) {
          assignment.parent = lastAtDepth[depth - 1].id;
        }

        assignments.push(assignment);
        lastAtDepth[depth] = assignment; // Update the last assignment at this depth
      });
      console.log('lastAtDepth', lastAtDepth);
      console.log('Created assignments:', assignments);
      return assignments;
    } catch (error) {
      console.error('Error reading assignments from XLS:', error);
      enqueueSnackbar('Virhe luettaessa litteroita tiedostosta', {
        variant: 'error',
      });
      return [];
    }
  };

  const writeAssignmentsToXLS = async (assignments, projectName) => {
    const childrenMap = new Map();

    assignments.forEach((assignment) => {
      childrenMap.set(assignment.id, []);
    });

    assignments.forEach((assignment) => {
      if (assignment.parent) {
        const parentChildren = childrenMap.get(assignment.parent);
        if (parentChildren) {
          parentChildren.push(assignment.id);
        }
      }
    });

    function buildRows(assignmentId, depth) {
      const assignment = assignments.find((a) => a.id === assignmentId);
      const row = Array(maxDepth + 1).fill(null);

      row[depth] = { value: assignment.number?.toString() || '' };
      row[depth + 1] = { value: assignment.name?.toString() || '' };
      row[depth + 2] = {
        value: assignment.unitAmountGoal?.toString() || '',
      };
      row[depth + 3] = { value: assignment.unit?.toString() || '' };
      row[depth + 4] = { value: assignment.unitPrice?.toString() || '' };

      data.push(row);

      const children = childrenMap.get(assignmentId) || [];
      children
        .sort((a, b) => a.number - b.number)
        .forEach((childId) => buildRows(childId, depth + 1));
    }

    const maxDepth = Math.max(
      ...assignments.map((assignment) => {
        let depth = 0;
        let parent = assignment.parent;
        while (parent) {
          depth++;
          parent = assignments.find((a) => a.id === parent)?.parent;
        }
        return depth + 1;
      })
    );

    const data = [];

    assignments
      .sort((a, b) => a.number - b.number)
      .forEach((assignment) => {
        if (!assignment.parent) {
          buildRows(assignment.id, 0);
        }
      });

    const HEADER_ROW = [
      {
        value: t('project_dialog.assignment_number'),
        fontWeight: 'bold',
      },
      {
        value: t('project_dialog.assignment_name'),
        fontWeight: 'bold',
      },
      {
        value: t('project_dialog.assignment_amount'),
        fontWeight: 'bold',
      },
      {
        value: t('project_dialog.assignment_unit'),
        fontWeight: 'bold',
      },
      {
        value: t('project_dialog.assignment_unit_price'),
        fontWeight: 'bold',
      },
    ];

    const rows = [HEADER_ROW, ...data];

    console.log('Writing assignments to XLS:', rows);

    await writeXlsxFile(rows, {
      fileName: `${projectName.replace(/\s+/g, '_')}_litterat.xlsx`,
      stickyRowsCount: 1,
    });
  };

  return {
    contactOptions,
    siteManagerOptions,
    getIlveability,
    getIntegrationPartners,
    readAssignmentsFromXLS,
    writeAssignmentsToXLS,
  };
}

export default useProjectDialog;
